/**
 * Groups the sidebar's top-level entries into labelled zones.
 *
 * Deliberately kept out of `menu-config.js` and keyed by that file's existing
 * `id` values: the menu config is edited often and by more than one person, and
 * this mapping needs no changes there at all. An id with no entry here falls
 * into the ungrouped bucket, so adding a menu item never breaks the sidebar —
 * it just appears below the grouped zones until it is assigned.
 */

export const SECTION_ORDER = ['commerce', 'operations', 'experience'];

/** i18n keys for each zone heading. */
export const SECTION_LABEL_KEYS = {
  commerce: 'section.commerce',
  operations: 'section.operations',
  experience: 'section.experience',
};

/** menu-config `id` -> zone. Entries not listed stay ungrouped. */
export const SECTION_BY_MENU_ID = {
  // Commerce — what the store sells and the money coming in.
  'order.management': 'commerce',
  'food.management': 'commerce', // legacy id for the Product group
  'used.management': 'commerce',

  // Operations — the work the shop does on devices.
  'repair.management': 'operations',
  'trade_in.management': 'operations',
  inventory_management: 'operations',

  // Experience — what the customer sees.
  storefront: 'experience',
  'content.management': 'experience',
  'restaurant.management_05': 'experience', // legacy id for Marketing
  media_library: 'experience',
};

/** Entries pinned above every zone, in the order given. */
export const PINNED_TOP_IDS = ['dashboard_01', 'tasks_board'];

/** Entries pinned below every zone (settings, sign out), in the order given. */
export const PINNED_BOTTOM_IDS = [
  'customer.management_02',
  'staff.management',
  'analytics',
  'business.settings_02',
  'system.settings_01',
  'logout_04',
];

/**
 * Splits a flat menu list into { top, sections, bottom } without reordering
 * within a zone, so the source order in menu-config still decides how items
 * read inside their own group.
 */
export function groupMenuBySection(menuList) {
  const items = Array.isArray(menuList) ? menuList : [];
  const byId = new Map(items.map((item) => [item?.id, item]));

  const top = PINNED_TOP_IDS.map((id) => byId.get(id)).filter(Boolean);
  const bottom = PINNED_BOTTOM_IDS.map((id) => byId.get(id)).filter(Boolean);
  const claimed = new Set([...top, ...bottom].map((item) => item.id));

  const sections = SECTION_ORDER.map((key) => ({
    key,
    labelKey: SECTION_LABEL_KEYS[key],
    items: items.filter(
      (item) => !claimed.has(item?.id) && SECTION_BY_MENU_ID[item?.id] === key,
    ),
  })).filter((section) => section.items.length > 0);

  sections.forEach((section) =>
    section.items.forEach((item) => claimed.add(item.id)),
  );

  // Anything the map does not know about still renders, just ungrouped.
  const ungrouped = items.filter((item) => !claimed.has(item?.id));

  return { top, sections, ungrouped, bottom };
}
